/**
 * Fitur berlangganan dari sisi dashboard: mana yang menyala menentukan menu
 * mana yang tampil dan halaman mana yang boleh dibuka.
 *
 * Kuncinya sama persis dengan katalog di API (`common/rbac/features.ts`) —
 * di sanalah kunci itu dipetakan ke modul permission dan ditegakkan pada tiap
 * request. Yang ada di berkas ini hanyalah pemetaan kunci ke alamat halaman,
 * yang memang cuma dashboard yang tahu.
 *
 * Menyaring menu di sini adalah kesantunan, bukan pengamanan: sumber
 * kebenarannya tetap FeatureGuard di server, yang menolak permintaan ke modul
 * yang tidak dibeli sekalipun seseorang mengetikkan alamatnya langsung.
 *
 * MENAMBAH FITUR BARU: daftarkan kuncinya di katalog API lebih dulu, lalu
 * tambahkan barisnya di sini beserta awalan alamat halamannya.
 */
export interface TenantFeature {
  key: string;
  label: string;
  /** Awalan alamat halaman yang ikut mati bila fitur ini tidak aktif. */
  paths: readonly string[];
}

export const TENANT_FEATURES = [
  { key: 'kehadiran', label: 'Kehadiran', paths: ['/kehadiran'] },
  { key: 'penilaian', label: 'Penilaian', paths: ['/penilaian'] },
  { key: 'kokurikuler', label: 'Kokurikuler', paths: ['/kokurikuler'] },
  { key: 'rapor', label: 'Rapor', paths: ['/rapor', '/rapor-penilaian'] },
  { key: 'sktb', label: 'SKTB', paths: ['/sktb'] },
  { key: 'jurnal', label: 'Jurnal Mengajar', paths: ['/jurnal'] },
  { key: 'portofolio', label: 'Portofolio', paths: ['/portofolio'] },
  { key: 'surat', label: 'Surat Menyurat', paths: ['/surat', '/sk'] },
  { key: 'pengumuman', label: 'Pengumuman', paths: ['/pengumuman'] },
  { key: 'kalender', label: 'Kalender', paths: ['/kalender'] },
  {
    key: 'keuangan',
    label: 'Keuangan',
    paths: ['/tagihan', '/pembayaran', '/pengeluaran', '/jenis-biaya', '/laporan-keuangan'],
  },
  { key: 'inventaris', label: 'Inventaris', paths: ['/inventaris'] },
  { key: 'broadcast', label: 'Broadcast', paths: ['/broadcast'] },
] as const satisfies readonly TenantFeature[];

/**
 * Fitur pemilik satu alamat halaman, atau null bila halaman itu bagian inti
 * yang tidak dijual terpisah.
 *
 * Pencocokan per ruas alamat, bukan `startsWith` biasa: "/rapor" tidak boleh
 * ikut mematikan "/raporan-lain" seandainya kelak ada halaman semacam itu.
 */
export function featureForPath(pathname: string): TenantFeature | null {
  return (
    TENANT_FEATURES.find((feature) =>
      feature.paths.some((path) => pathname === path || pathname.startsWith(`${path}/`)),
    ) ?? null
  );
}
